import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Loader, Message, Modal, Table } from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';

const asNonNegativeInteger = (value) => Math.max(0, Math.floor(Number(value) || 0));

const messageFromError = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' ');
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
};

const KitDetailModal = ({ open, kitId, authUser, onClose, onUndone }) => {
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmingUndo, setConfirmingUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);

  useEffect(() => {
    if (!open || !kitId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setConfirmingUndo(false);
    stockyApi.getKit(authUser, kitId)
      .then((response) => {
        if (!cancelled) setKit(response?.data || null);
      })
      .catch((requestError) => {
        if (!cancelled) setError(messageFromError(requestError, 'No pudimos cargar los detalles del kit.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, kitId, authUser]);

  const stockByItemId = useMemo(
    () => new Map((kit?.stock?.perComponent || []).map((component) => [String(component.itemId), component])),
    [kit],
  );
  const components = kit?.kitComponents || [];
  const canUndo = kit?.undo?.canUndo === true;

  const handleUndo = async () => {
    setUndoing(true);
    setError('');
    try {
      const response = await stockyApi.undoKit(authUser, kitId);
      onUndone?.(response?.data || {});
      onClose?.();
    } catch (requestError) {
      setConfirmingUndo(false);
      setError(messageFromError(requestError, 'No pudimos deshacer el kit. Inténtalo nuevamente.'));
    } finally {
      setUndoing(false);
    }
  };

  return (
    <Modal open={open} onClose={undoing ? undefined : onClose} size="small" closeIcon={!undoing}>
      <Modal.Header>
        <Icon name="cubes" color="violet" /> Detalle del kit
      </Modal.Header>
      <Modal.Content scrolling>
        {loading ? (
          <div style={{ minHeight: 220, display: 'grid', placeItems: 'center', gap: 10 }}>
            <Loader active inline="centered" />
            <span style={{ color: '#667085' }}>Cargando composición y disponibilidad…</span>
          </div>
        ) : error ? (
          <Message negative content={error} />
        ) : kit ? (
          <div style={{ display: 'grid', gap: 18 }}>
            <div>
              <h3 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={kit.title}>
                {kit.title || 'Kit sin título'}
              </h3>
              <div style={{ color: '#667085', marginTop: 4 }}>SKU: {kit.sku || 'Sin SKU'}</div>
            </div>

            <Message info style={{ margin: 0 }}>
              <Message.Header>{asNonNegativeInteger(kit.stock?.stock)} kits disponibles</Message.Header>
              <p style={{ marginBottom: 0 }}>La disponibilidad se calcula automáticamente con el componente que menos kits permite armar.</p>
            </Message>

            <div>
              <h4 style={{ margin: '0 0 8px' }}>Componentes</h4>
              <Table compact celled unstackable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Producto</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Por kit</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">En stock</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Permite armar</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {components.map((component) => {
                    const stock = stockByItemId.get(String(component.componentItemId)) || {};
                    const isBottleneck = String(kit.stock?.bottleneckItemId || '') === String(component.componentItemId);
                    return (
                      <Table.Row key={component.id || component.componentItemId} positive={isBottleneck}>
                        <Table.Cell>
                          <strong>{component.componentItem?.title || 'Producto sin título'}</strong>
                          <div style={{ color: '#667085', fontSize: 12 }}>SKU: {component.componentItem?.sku || 'Sin SKU'}</div>
                        </Table.Cell>
                        <Table.Cell textAlign="right">{asNonNegativeInteger(component.quantity)}</Table.Cell>
                        <Table.Cell textAlign="right">{asNonNegativeInteger(stock.available)}</Table.Cell>
                        <Table.Cell textAlign="right">{asNonNegativeInteger(stock.kitsAllowed)}</Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>

            <Message warning style={{ margin: 0 }}>
              <Message.Header>Deshacer este kit</Message.Header>
              <p>
                Restaurará {kit.listings?.length || 0} publicación{kit.listings?.length === 1 ? '' : 'es'} a su producto simple original y dará de baja el kit. No modifica el stock físico de los componentes.
              </p>
              {!canUndo ? <p style={{ marginBottom: 0 }}>{kit.undo?.blockedReason || 'Este kit no se puede deshacer de forma segura.'}</p> : null}
            </Message>
          </div>
        ) : null}
      </Modal.Content>
      <Modal.Actions>
        {confirmingUndo ? (
          <>
            <span style={{ float: 'left', color: '#9f3a38', paddingTop: 9 }}>Esta acción no se puede revertir automáticamente.</span>
            <Button disabled={undoing} onClick={() => setConfirmingUndo(false)}>Cancelar</Button>
            <Button negative loading={undoing} disabled={undoing} onClick={handleUndo}>
              Sí, deshacer kit
            </Button>
          </>
        ) : (
          <>
            <Button disabled={undoing} onClick={onClose}>Cerrar</Button>
            <Button negative disabled={!kit || !canUndo || undoing} onClick={() => setConfirmingUndo(true)}>
              <Icon name="undo" /> Deshacer kit
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default KitDetailModal;
